import React, { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputTaxJarConfig } from "lib/types/generated/graphql-types"
import { useLazyQuery, useMutation } from "@apollo/client"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"

import { CREATE_OR_UPDATE_TAX_JAR_CONFIG } from "graphql/mutations/config/Providers/TaxJar/CreateOrUpdateTaxJarConfig"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"
import { TaxJarConfigInputs } from "../../../../components/Config/TaxJar/TaxJarConfigInputs"
import { VALIDATE_TAX_JAR } from "../../../../graphql/queries/config/Providers/TaxJar/ValidateTaxJarConfig"
import { handleErrorMessages } from "helpers/errors"

type Props = {
  refetch: () => void
}

interface ExtendedInputTaxJarConfig {
  id: string
  taxJarConfig: InputTaxJarConfig
}

export const CreateTaxJarConfig = ({ refetch }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ExtendedInputTaxJarConfig>({ mode: "onBlur" })

  const [addTaxJarConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_TAX_JAR_CONFIG,
    {
      onCompleted: () => {
        reset({
          id: "",
          taxJarConfig: {
            apiKey: "",
            baseUrl: ""
          }
        })
        dispatch(hideEditSidebar())
        dispatch(
          alertActions.actions.createAlert({
            message: "TaxJar configuration successfully created",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )

  const [validateTaxJar, { loading }] = useLazyQuery(VALIDATE_TAX_JAR, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputTaxJarConfig> = (data) => {
    const { baseUrl, apiKey } = data.taxJarConfig
    validateTaxJar({
      variables: {
        input: {
          baseUrl,
          apiKey
        }
      }
    }).then((response) => {
      if (response.data.Configuration.validate.taxJar.valid) {
        addTaxJarConfig({
          variables: {
            id: data.id,
            taxJarConfig: {
              baseUrl,
              apiKey
            }
          }
        })
      } else {
        setValidationError(true)
      }
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar>
        <EditSidebarHeader title="Create TaxJar configuration">
          <PrimaryButton type="submit" loading={createLoading || loading}>
            Save
          </PrimaryButton>
        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with TaxJar"
            cancelButtonText={"Close"}
            hideOkButton={true}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <TaxJarConfigInputs control={control} errors={errors} />
      </EditSidebar>
    </form>
  )
}
